import { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import useAuthStore from "../store/authStore";
import api from "../api/client";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  MapPin,
  Briefcase,
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
} from "lucide-react";

export default function SubmitToPosition() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const refSlug = searchParams.get("ref");
  const navigate = useNavigate();
  const { seekerUser, seekerToken } = useAuthStore();

  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [consented, setConsented] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Position";
  }, []);

  useEffect(() => {
    const fetchPosition = async () => {
      try {
        const res = await api.get(
          `/job-positions/by-token/${token}${refSlug ? `?ref=${encodeURIComponent(refSlug)}` : ""}`,
        );
        setPosition(res.data);
      } catch (err) {
        setError(
          err.response?.status === 410
            ? "This position is no longer accepting submissions."
            : "Position not found.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPosition();
  }, [token]);

  const formatLocation = () => {
    if (!position) return "";
    const parts = [];
    if (position.location_city) parts.push(position.location_city);
    if (position.location_country) parts.push(position.location_country);
    if (position.is_remote) parts.push("Remote");
    return parts.join(" · ") || "Location not specified";
  };

  const formatSalary = () => {
    if (!position?.salary_min && !position?.salary_max) return null;
    const cur = position.salary_currency || "EUR";
    if (position.salary_min && position.salary_max) {
      return `${cur} ${position.salary_min.toLocaleString()} – ${position.salary_max.toLocaleString()}`;
    }
    return `${cur} ${(position.salary_min || position.salary_max).toLocaleString()}+`;
  };

  const formatCloses = () => {
    if (!position?.closes_at) return null;
    const d = new Date(position.closes_at);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (!consented) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = refSlug ? { referred_by_slug: refSlug } : {};
      const res = await api.post(
        `/job-positions/by-token/${token}/submit`,
        payload,
        {
          headers: { Authorization: `Bearer ${seekerToken}` },
        },
      );
      setSubmitted(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const referralUrl = `${window.location.origin}/submit/${token}?ref=${seekerUser?.resume_slug}`;
  const copyReferralUrl = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // Render markdown — minimal, safe-ish rendering
  const renderMarkdown = (text) => {
    if (!text) return null;
    // Simple markdown: paragraphs, bold, italic, headings, bullets
    return text.split("\n\n").map((para, i) => {
      const trimmed = para.trim();
      if (trimmed.startsWith("## ")) {
        return (
          <h2
            key={i}
            className="text-xl font-semibold mt-6 mb-3 text-gray-900 dark:text-white"
          >
            {trimmed.slice(3)}
          </h2>
        );
      }
      if (trimmed.startsWith("# ")) {
        return (
          <h1
            key={i}
            className="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-white"
          >
            {trimmed.slice(2)}
          </h1>
        );
      }
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        const items = trimmed
          .split("\n")
          .map((line) => line.replace(/^[-*]\s/, ""));
        return (
          <ul key={i} className="list-disc list-inside space-y-1 my-3">
            {items.map((item, j) => (
              <li
                key={j}
                className="text-gray-700 dark:text-gray-300 leading-relaxed"
              >
                {renderInline(item)}
              </li>
            ))}
          </ul>
        );
      }
      return (
        <p
          key={i}
          className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3"
        >
          {renderInline(trimmed)}
        </p>
      );
    });
  };

  const renderInline = (text) => {
    // Bold, italic
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-brand-dark-bg">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !position) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
        <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
          <Logo className="h-10 sm:h-12 flex-shrink-0" />
          <ThemeToggle />
        </nav>
        <main className="max-w-2xl mx-auto px-6 py-20 text-center">
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
            {error}
          </p>
          <Link to="/" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
            Back to Joborigo
          </Link>
        </main>
      </div>
    );
  }

  // Submitted confirmation screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
        <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
          <Logo className="h-10 sm:h-12 flex-shrink-0" />
          <ThemeToggle />
        </nav>
        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Submission received</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Your application has been added to {position.posted_by_name}'s
              pipeline. You'll see status updates on your Joborigo dashboard the
              moment they happen.
            </p>
            <button
              onClick={() => navigate("/app/dashboard")}
              className="px-4 py-2 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
            >
              Go to your dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Main rendering — Talent Brief style
  const salary = formatSalary();
  const closes = formatCloses();
  const referredByName = position?.referrer_name ?? null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
        <Logo className="h-10 sm:h-12 flex-shrink-0" />
        <div className="flex items-center gap-4 text-sm">
          <ThemeToggle />
          {!seekerUser && (
            <>
              <Link
                to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className="hidden sm:inline-block text-gray-600 dark:text-gray-300 hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
              >
                Sign in
              </Link>
              <Link
                to={`/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className="bg-brand-violet text-white px-3 sm:px-4 py-1.5 rounded-md hover:opacity-90 transition whitespace-nowrap"
              >
                Get started
              </Link>
            </>
          )}
          {seekerUser && (
            <span className="hidden sm:inline-block text-gray-600 dark:text-gray-300">
              {seekerUser.full_name}
            </span>
          )}
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Posted-by header */}
        <p className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-2">
          Talent Brief — Presented by {position.posted_by_name}
        </p>

        {/* Title block */}
        <div className="mb-8">
          {position.client_company_name && (
            <p className="text-lg text-gray-500 dark:text-gray-400 mb-1">
              {position.client_company_name}
            </p>
          )}
          <h1 className="text-4xl font-extrabold mb-4 leading-tight">
            {position.title}
          </h1>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-surface text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" />
              {position.employment_type}
            </span>
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-surface text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              {formatLocation()}
            </span>
            {salary && (
              <span className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-surface text-gray-700 dark:text-gray-300">
                {salary}
              </span>
            )}
            {closes && (
              <span className="text-xs px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                <CalendarDays className="w-3 h-3" />
                Closes {closes}
              </span>
            )}
          </div>
        </div>

        {/* Referral attribution */}
        {refSlug && (
          <div className="bg-brand-violet/5 dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-5 py-3 mb-6">
            <p className="text-sm text-brand-violet dark:text-brand-violet-on-dark">
              Referred by {referredByName} on Joborigo
            </p>
          </div>
        )}

        {/* Description */}
        {position.description ? (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-6">
            {renderMarkdown(position.description)}
          </div>
        ) : (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No description provided. Reach out to {position.posted_by_name}{" "}
              for more details.
            </p>
          </div>
        )}

        {/* Employer message */}
        {position.employer_message && (
          <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-5 py-4 mb-6">
                      <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark mb-1 uppercase tracking-widest">
              From {position.posted_by_name}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {position.employer_message}
            </p>
          </div>
        )}

        {/* Submission form */}
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border-2 border-brand-violet/30 dark:border-brand-violet-on-dark/30 p-6">
          <h2 className="text-lg font-semibold mb-3">Submit your resume</h2>

          {!seekerUser ? (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Sign in or create an account to submit your resume to this
                position. Joborigo lets you track every application in
                real-time, with status updates pushed directly to your
                dashboard.
              </p>
              <div className="flex gap-3">
                <Link
                  to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="flex-1 py-2 text-center border border-gray-200 dark:border-white/10 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                  Sign in
                </Link>
                <Link
                  to={`/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="flex-1 py-2 text-center bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
                >
                  Create account
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Submitting as <strong>{seekerUser.full_name}</strong> ·{" "}
                <span className="text-gray-400">{seekerUser.email}</span>
              </p>

              <label className="flex items-start gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  I agree that {position.posted_by_name}
                  {position.posted_by_is_agency
                    ? " may review my profile and may share an anonymized version with their clients during the hiring process. My identity will only be revealed to clients who indicate genuine interest in my candidacy."
                    : " may review my profile and resume for this position."}
                </span>
              </label>

              <button
                onClick={handleSubmit}
                disabled={!consented || submitting}
                className="w-full py-2 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? "Submitting..." : "Submit resume"}
              </button>

              {seekerUser && position.allow_peer_referrals && (
                <div className="mt-6 pt-6 border-t border-gray-100 dark:border-white/10">
                  <p className="text-sm font-medium mb-1">Not a fit for you?</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Share this with a peer who might be — they'll be added to
                    the pipeline with attribution to you.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={referralUrl}
                      readOnly
                      className="flex-1 bg-gray-50 dark:bg-brand-dark-bg rounded px-2 py-1 text-xs font-mono text-brand-violet dark:text-brand-violet-on-dark break-all"
                    />
                    <button
                      onClick={copyReferralUrl}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 text-xs rounded-md hover:bg-gray-200 dark:hover:bg-white/10 transition"
                    >
                      {copiedUrl ? (
                        <>
                          <Check className="w-3 h-3" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
